import React, { useState, useEffect } from 'react';
import { Crate } from '@perch-labs/shrike';
import { copyFor } from 'config';
import { TogglePlan } from '../../secondary/payment-method/payment-method.api';
import {
  getInitialHash,
  getMutationVariables,
  MutationVariables,
} from './plan-toggles.utils';
import { PlanSwitch, AllPlansSwitch } from './plan-toggles.style';

interface Props {
  accountId: number;
  plans: TogglePlan[];
  setMutationVariables: (variables: MutationVariables | undefined) => void;
  isActivePlanWhale: boolean;
  paymentMethodId: number;
}

const getCopy = copyFor('settings.paymentInformation.planToggles');

export const PlanToggles = ({
  accountId,
  plans,
  setMutationVariables,
  isActivePlanWhale,
  paymentMethodId,
}: Props) => {
  const [shouldUpdateAllPlans, setShouldUpdateAllPlans] = useState(false);
  const initialHash = getInitialHash(plans);
  const [plansHash, setPlansHash] = useState(initialHash);

  useEffect(() => {
    setMutationVariables(
      getMutationVariables({
        plansHash,
        shouldUpdateAllPlans,
        accountId,
        paymentMethodId,
        plans,
      })
    );
  }, [plansHash, shouldUpdateAllPlans]);

  const togglePlan = planId => {
    const currentValue = plansHash[planId];
    setPlansHash({ ...plansHash, [planId]: !currentValue });
    // if current value is true, we're setting a val to false and should turn off the allPlans toggle
    if (currentValue) {
      setShouldUpdateAllPlans(false);
    } else if (
      // else check if this is the last plan we're toggling on. if so, toggle allPlans true
      !Object.keys(plansHash).find(
        id => !plansHash[id] && Number(id) !== planId
      )
    ) {
      setShouldUpdateAllPlans(true);
    }
  };

  if (isActivePlanWhale) {
    return (
      <AllPlansSwitch
        checked={shouldUpdateAllPlans}
        onChange={() => setShouldUpdateAllPlans(!shouldUpdateAllPlans)}
        label={getCopy('allPlans')}
        margin={{ bottom: '72px' }}
      />
    );
  }

  const showUpdateAllPlans = plans.length > 1;

  return (
    <Crate width="100%">
      {showUpdateAllPlans && (
        <AllPlansSwitch
          checked={shouldUpdateAllPlans}
          onChange={() => {
            // the account val should also set the val for the other plans
            plans.forEach(plan => {
              plansHash[plan.id] = !shouldUpdateAllPlans;
            });
            setShouldUpdateAllPlans(!shouldUpdateAllPlans);
          }}
          label={getCopy('allPlans')}
          margin={{ bottom: '24px' }}
        />
      )}
      {plans.map(plan => {
        return (
          <PlanSwitch
            key={plan.id}
            checked={plansHash[plan.id] === true}
            onChange={() => togglePlan(plan.id)}
            label={plan.displayName}
          />
        );
      })}
    </Crate>
  );
};
